import { REPORT_WHO_MAX, REPORT_WHO_SEPARATOR, type ReportRangeView, type ReportsQuery } from "@gioia365/contracts";
import { dayParam, PERIOD_PARAMS, personParam } from "../conversations/conversation-search";

// L'indirizzo del Report (piano M7, Task 3; D11; mock-up `report.html`): la scheda, il periodo e «Di chi» stanno nell'indirizzo
// (`/report?periodo=settimana&chi=<id>,<id>`), così un link o il ritorno con il browser riaprono la stessa vista; il gruppo di colonne
// delle tabelle resta nello stato della pagina. I periodi sono quelli di «Conversazioni» (SEZ-3), con le stesse parole e gli stessi
// controlli dei giorni e delle persone (`conversation-search.ts`), tranne «Tutte»; quello di partenza è il «Mese». I valori di partenza
// («Commerciali», «Mese», «Tutto il team») non si scrivono; un valore sconosciuto o di forma sbagliata vale come assente.
// «Personalizzato» porta i due giorni («Dal» e «Al», `AAAA-MM-GG`): senza tutti e due, o con un giorno che non esiste, vale «Mese»;
// al contrario si leggono in ordine. Un indirizzo non ha mai un periodo rovesciato: lo chiede soltanto un link scritto a mano, perché la
// pagina, prima di scriverlo, dice l'errore sotto i campi e lascia i numeri come sono (`ReportControls.tsx`). «Di chi» sono al più 50
// persone, una volta ciascuna. La scheda «Campagne» è `vista=campagne` (Task 5); senza, «Commerciali».

/** I periodi del Report, con le parole dell'indirizzo di «Conversazioni» e quelle del server; «Mese» è quello di partenza. */
export const REPORT_PERIOD_PARAMS = {
  oggi: PERIOD_PARAMS.oggi,
  ieri: PERIOD_PARAMS.ieri,
  settimana: PERIOD_PARAMS.settimana,
  mese: PERIOD_PARAMS.mese,
  personalizzato: PERIOD_PARAMS.personalizzato,
} as const satisfies Record<string, ReportsQuery["period"]>;
export type ReportPeriodParam = keyof typeof REPORT_PERIOD_PARAMS;

/** Il periodo di partenza, che l'indirizzo non scrive. */
export const DEFAULT_REPORT_PERIOD = "mese" satisfies ReportPeriodParam;

/**
 * Quanti giorni al più ha un «Personalizzato», come il server (`REPORT_CUSTOM_MAX_DAYS` del dominio, `422 report_period_invalid` con
 * `too_long`): la pagina dice l'errore prima di chiedere. Un test lo tiene uguale a quello del dominio (`report-search.test.ts`).
 */
export const CUSTOM_PERIOD_MAX_DAYS = 366;

/** I periodi nell'ordine di SEZ-3, come i pulsanti. */
export const REPORT_PERIOD_ORDER: readonly ReportPeriodParam[] = ["oggi", "ieri", "settimana", "mese", "personalizzato"];

export type ReportsSearch = {
  /** La scheda: senza, «Commerciali»; `campagne` per «Campagne». */
  vista?: "campagne";
  periodo?: Exclude<ReportPeriodParam, typeof DEFAULT_REPORT_PERIOD>;
  /** «Dal» e «Al» di «Personalizzato» (`AAAA-MM-GG`, «Dal» mai dopo «Al»): ci sono soltanto con quel periodo, tutti e due. */
  dal?: string;
  al?: string;
  /** «Di chi»: le appartenenze scelte, separate da una virgola; senza, «Tutto il team». Vale soltanto per chi vede il team. */
  chi?: string;
};

const isPeriod = (value: unknown): value is ReportPeriodParam => typeof value === "string" && Object.hasOwn(REPORT_PERIOD_PARAMS, value);

/** Il periodo dell'indirizzo, con i due giorni di «Personalizzato» in ordine: senza tutti e due, «Mese» (nulla da scrivere). */
function periodOf(raw: Record<string, unknown>): Pick<ReportsSearch, "periodo" | "dal" | "al"> {
  const word = raw.periodo;
  if (!isPeriod(word) || word === DEFAULT_REPORT_PERIOD) return {};
  if (word !== "personalizzato") return { periodo: word };
  const dal = dayParam(raw.dal);
  const al = dayParam(raw.al);
  if (dal === undefined || al === undefined) return {};
  // Due giorni `AAAA-MM-GG` si confrontano come testi.
  return dal <= al ? { periodo: word, dal, al } : { periodo: word, dal: al, al: dal };
}

/** Le persone di «Di chi»: identificativi possibili, una volta ciascuno, al più 50, nell'ordine dell'indirizzo. */
function chosenParam(value: unknown): string[] {
  const parts = typeof value === "string" ? value.split(REPORT_WHO_SEPARATOR) : [value];
  const ids = new Set<string>();
  for (const part of parts) {
    const id = personParam(part);
    if (id !== undefined) ids.add(id);
    if (ids.size === REPORT_WHO_MAX) break;
  }
  return [...ids];
}

/** I parametri dell'indirizzo `/report`: un valore sconosciuto o di forma sbagliata vale come assente. */
export function validateReportsSearch(raw: Record<string, unknown>): ReportsSearch {
  const result: ReportsSearch = { ...(raw.vista === "campagne" ? { vista: "campagne" as const } : {}), ...periodOf(raw) };
  const chosen = chosenParam(raw.chi);
  if (chosen.length > 0) result.chi = chosen.join(REPORT_WHO_SEPARATOR);
  return result;
}

/** La richiesta per il server: il periodo (con «Personalizzato» anche `from` e `to`) e, con persone scelte, `who`. */
export function reportsQueryOf(search: ReportsSearch): ReportsQuery {
  const period = REPORT_PERIOD_PARAMS[search.periodo ?? DEFAULT_REPORT_PERIOD];
  let query: ReportsQuery;
  if (period !== "custom") query = { period };
  else if (search.dal !== undefined && search.al !== undefined) query = { period, from: search.dal, to: search.al };
  else query = { period: REPORT_PERIOD_PARAMS[DEFAULT_REPORT_PERIOD] };
  if (search.chi) query.who = search.chi;
  return query;
}

/** Le persone scelte in «Di chi», nell'ordine dell'indirizzo; nessuna vuol dire «Tutto il team». */
export function chosenOf(search: ReportsSearch): string[] {
  return search.chi ? search.chi.split(REPORT_WHO_SEPARATOR) : [];
}

/** Il giorno `AAAA-MM-GG` spostato di `days` giorni di calendario (un giorno di calendario non ha fuso). */
export function shiftDay(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Quanti giorni di calendario vanno da `from` a `to`, tutti e due compresi. */
export function dayCountOf(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / (24 * 60 * 60_000)) + 1;
}

/** Il primo e l'ultimo giorno del periodo dell'indirizzo, contando da `today` («Settimana» e «Mese»: oggi e i 6 o 29 giorni prima). */
export function periodDaysOf(search: ReportsSearch, today: string): { from: string; to: string } {
  switch (search.periodo ?? DEFAULT_REPORT_PERIOD) {
    case "oggi":
      return { from: today, to: today };
    case "ieri":
      return { from: shiftDay(today, -1), to: shiftDay(today, -1) };
    case "settimana":
      return { from: shiftDay(today, -6), to: today };
    case "personalizzato":
      if (search.dal !== undefined && search.al !== undefined) return { from: search.dal, to: search.al };
      return { from: shiftDay(today, -29), to: today };
    default:
      return { from: shiftDay(today, -29), to: today };
  }
}

/**
 * Il periodo e quello prima come li dirà il server (`range`, `previous` di `GET /api/reports/sales`), per scriverli mentre la
 * risposta arriva: lo stesso numero di giorni subito prima e, se il periodo arriva a oggi, «fino alle» l'ora di adesso. Servono
 * soltanto alle frasi; i numeri e i giorni veri sono quelli della risposta.
 */
export function expectedRangesOf(search: ReportsSearch, today: string, now: string): { range: ReportRangeView; previous: ReportRangeView } {
  const { from, to } = periodDaysOf(search, today);
  const length = dayCountOf(from, to);
  const endsAt = to === today ? now : null;
  return { range: { from, to, endsAt }, previous: { from: shiftDay(from, -length), to: shiftDay(from, -1), endsAt } };
}
